import { useEffect, useState } from "react";
import { Wallet, Trash2, RefreshCw, Phone, Video, MessageSquare, Gift, Package } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fmtDate } from "@/lib/api";

const CATS = [["all", "എല്ലാം", Package], ["audio", "ഓഡിയോ കോൾ", Phone], ["video", "വീഡിയോ കോൾ", Video], ["chat", "ചാറ്റ്", MessageSquare], ["gift", "ഗിഫ്റ്റ്", Gift], ["other", "മറ്റുള്ളവ", Package]];

export const DailyDeposits = () => {
  const [data, setData] = useState(null);
  const load = () => api.get("/admin/reports/daily-deposits?days=14").then((r) => setData(r.data)).catch(() => null);
  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, []);
  if (!data) return null;
  return (
    <div data-testid="daily-deposits" className="card rounded-2xl p-5 space-y-3">
      <div className="flex items-center justify-between">
        <p className="label-caps inline-flex items-center gap-1.5"><Wallet size={12} /> ഡെയ്‌ലി കസ്റ്റമർ ഡെപ്പോസിറ്റ് (അഡ്മിൻ ആഡ്)</p>
        <button type="button" data-testid="daily-deposits-refresh" onClick={load} className="btn-ghost w-7 h-7 rounded-full inline-flex items-center justify-center"><RefreshCw size={12} /></button>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-3 text-center"><p className="text-[10px] font-bold text-emerald-700">ഇന്ന് ആഡ് ചെയ്തത്</p><p data-testid="deposits-today" className="font-num text-xl font-extrabold text-emerald-700">₹{data.today_inr}</p></div>
        <div className="rounded-xl bg-sky-50 border border-sky-200 p-3 text-center"><p className="text-[10px] font-bold text-sky-700">14 ദിവസം ആകെ</p><p data-testid="deposits-total" className="font-num text-xl font-extrabold text-sky-700">₹{data.total_inr}</p></div>
      </div>
      <ul className="divide-y divide-[var(--line)] max-h-56 overflow-auto">
        {data.days.length === 0 && <li className="text-xs text-[var(--muted-text)] py-2">റെക്കോർഡുകൾ ഇല്ല</li>}
        {data.days.map((d) => <li key={d.day} data-testid={`deposit-day-${d.day}`} className="flex items-center justify-between py-2 text-xs"><span className="font-num text-[var(--muted-text)]">{d.day}</span><span>{d.count} തവണ</span><span className="font-num font-extrabold text-emerald-700">+₹{d.inr}</span></li>)}
      </ul>
    </div>
  );
};

export const PaymentRecords = () => {
  const [rows, setRows] = useState([]);
  const [cat, setCat] = useState("all");
  const load = () => api.get("/admin/payments").then((r) => setRows(r.data)).catch(() => null);
  useEffect(() => { load(); }, []);
  const del = async (r) => {
    if (!window.confirm(`${r.customer_name || r.username} — ₹${r.amount_inr} (${r.label || "—"}) റെക്കോർഡ് ഡിലീറ്റ് ചെയ്യണോ?`)) return;
    try { await api.delete(`/admin/recharges/${r.id}`); toast.success("ഡിലീറ്റ് ചെയ്തു"); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const filtered = rows.filter((r) => cat === "all" || r.category === cat);
  return (
    <div data-testid="payment-records" className="card rounded-2xl p-5 space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="label-caps">സർവീസ് തിരിച്ചുള്ള പേയ്‌മെന്റ് റെക്കോർഡുകൾ ({filtered.length})</p>
        <button type="button" data-testid="payment-records-refresh" onClick={load} className="btn-ghost w-7 h-7 rounded-full inline-flex items-center justify-center"><RefreshCw size={12} /></button>
      </div>
      <div className="flex flex-wrap gap-1.5">
        {CATS.map(([v, l, I]) => <button key={v} type="button" data-testid={`pay-cat-${v}`} onClick={() => setCat(v)} className={`rounded-full h-8 px-3 text-[11px] font-bold inline-flex items-center gap-1 border transition-colors ${cat === v ? "bg-[var(--accent-c)] text-white border-[var(--accent-c)]" : "bg-white border-[var(--line)]"}`}><I size={11} /> {l}</button>)}
      </div>
      <ul className="divide-y divide-[var(--line)] max-h-96 overflow-auto">
        {filtered.length === 0 && <li className="text-xs text-[var(--muted-text)] py-3">റെക്കോർഡുകൾ ഇല്ല</li>}
        {filtered.map((r) => (
          <li key={r.id} data-testid={`payment-row-${r.id}`} className="py-2 flex flex-wrap items-center gap-2 text-xs">
            <span className="font-num text-[var(--muted-text)]">{fmtDate(r.created_at)}</span>
            <span className="font-bold">{r.customer_name || r.username}</span>
            <span className="rounded-full bg-sky-50 text-sky-700 px-2 py-0.5 text-[10px] font-bold">{r.label || r.category}</span>
            <span className="font-num font-extrabold">₹{r.amount_inr}</span>
            <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${r.status === "approved" ? "bg-emerald-50 text-emerald-700" : r.status === "rejected" ? "bg-red-50 text-red-600" : "bg-amber-50 text-amber-700"}`}>{(r.status || "").toUpperCase()}</span>
            <button type="button" data-testid={`payment-delete-${r.id}`} onClick={() => del(r)} className="ml-auto w-8 h-8 rounded-full bg-red-50 text-red-500 inline-flex items-center justify-center hover:bg-red-100"><Trash2 size={13} /></button>
          </li>
        ))}
      </ul>
    </div>
  );
};
